<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, onActivated } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useContactStore } from '../stores/contacts.store';
import { useDialog } from '@/shared/composables/useDialog';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import ContactFileUploadPage from './ContactFileUploadPage.vue';
import ContactCreateIndividualPage from './ContactCreateIndividualPage.vue';
import ContactCreateCorporatePage from './ContactCreateCorporatePage.vue';
import ContactDetailPage from './ContactDetailPage.vue';
import ContactMergeDialog from '../components/ContactMergeDialog.vue';
import type { ContactListParams, ContactRegisterType } from '../types/contact.types';
import type { ContactDeleteBlocker } from '../types/contact.types';
import { contactsApi } from '../api/contactsApi';
import type { VDataTable } from 'vuetify/components';
import { usePermissionGuard } from '@/shared/composables/usePermissionGuard';

type DataTableHeader = NonNullable<VDataTable['$props']['headers']>[number];

// Props 정의
interface Props {
  isModal?: boolean;
  modelValue?: boolean;
  initialSearch?: string;
  initialCategory?: string; // 'B09001' (Corporate) or 'B09002' (Individual)
  onSelect?: (contact: any) => void;
}

const props = withDefaults(defineProps<Props>(), {
  isModal: false,
  modelValue: false,
  initialSearch: '',
  initialCategory: '',
  onSelect: undefined
});

// Emits 정의
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'select': [contact: any];
}>();

const router = useRouter();
const route = useRoute();
const contactStore = useContactStore();
const { alert, confirm } = useDialog();
const sessionStore = useSessionStore();
const { guardAndRun, createGuardedAction } = usePermissionGuard();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

// 언어 변경 이벤트 핸들러 (named reference - onBeforeUnmount 에서 제거 가능)
const handleLanguageChanged = (event: any) => {
    currentLanguage.value = event.detail.language;
};

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChanged);

    // initialCategory가 있으면 초기 로드 시 필터링 적용
    if (props.initialCategory) {
        selectedCategory.value = props.initialCategory;
    }
});

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const selectedCategory = ref(props.initialCategory || '');
const selectedContactType = ref<ContactRegisterType | ''>('');

// ============================================================
// 성능 전략: 프리페치 + 프론트 캐시 하이브리드
//   - 검색어 없음  → 서버사이드 페이징 (API: page/limit) - 빠름
//   - 검색어 있음  → 프리페치 캐시에서 in-memory 필터링 - 즉시
//   - 캐시 미완성  → 서버 검색 fallback (app-level LIKE, 3초)
//   - Create/Update/Delete → 캐시 무효화 + 재프리페치
// ============================================================
const serverTotal = ref(0);
const allContactsCache = ref<any[] | null>(null);
const isPrefetching = ref(false);

/** 검색 비교용: NBSP·기타 유니코드 공백을 일반 스페이스로 통일 후 연속 공백 축소 (`includes` 미스 방지) */
function normalizeContactSearchKey(raw: unknown): string {
    return String(raw ?? '')
        .replace(/\u00a0/g, ' ')
        .replace(/[\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

/** 백엔드 GET /api/Contact 앱 필터와 동일하게 이름 후보 문자열 구성 */
function contactSearchableFullName(c: any): string {
    const merged = [c?.CONTACT_NAME, c?.FIRST_NAME, c?.SURNAME]
        .filter((p) => p != null && String(p).trim() !== '')
        .join(' ');
    return normalizeContactSearchKey(merged);
}

// 화면에 보여줄 contacts
// - 검색 모드: 캐시에서 필터+slice
// - 일반 모드: 서버가 반환한 현재 페이지 그대로
const isSearchMode = computed(() => (searchQuery.value || '').trim().length > 0);

const filteredCached = computed(() => {
    if (!isSearchMode.value || !allContactsCache.value) return [];
    const q = normalizeContactSearchKey(searchQuery.value);
    if (!q) return [];
    return allContactsCache.value.filter((c: any) => {
        // Category 서버필터를 캐시에도 반영
        if (selectedCategory.value && c.CATEGORY_CD !== selectedCategory.value) return false;
        if (props.initialCategory && !selectedCategory.value && c.CATEGORY_CD !== props.initialCategory) return false;
        const fullName = contactSearchableFullName(c);
        const email = normalizeContactSearchKey(c.CONTACT_EMAIL || '');
        const regNo = normalizeContactSearchKey(c.CONTACT_REG_NO || '');
        return fullName.includes(q) || email.includes(q) || regNo.includes(q);
    });
});

const paginatedContacts = computed(() => {
    if (isSearchMode.value && allContactsCache.value) {
        // 캐시 기반 페이지네이션
        const start = (currentPage.value - 1) * itemsPerPage.value;
        const end = start + itemsPerPage.value;
        return filteredCached.value.slice(start, end);
    }
    // 서버사이드 페이징 결과 그대로
    return contactStore.contacts;
});

const filteredTotal = computed(() => {
    if (isSearchMode.value && allContactsCache.value) {
        return filteredCached.value.length;
    }
    return serverTotal.value;
});

// 이미지에 맞춘 컬럼: No., Category, Name, Email, Registration Number, Nearest Expiry Date, Registrant, Option
const headers = computed<DataTableHeader[]>(() => [
    { title: 'No.', key: 'no', sortable: false, width: '80px' },
    { title: t('contact.Category') || 'Category', key: 'CATEGORY_CD', sortable: true },
    { title: t('contact.Name') || 'Name', key: 'CONTACT_NAME', sortable: true },
    { title: t('contact.Email') || 'Email', key: 'CONTACT_EMAIL', sortable: false },
    { title: t('contact.Registration Number') || 'Registration Number', key: 'CONTACT_REG_NO', sortable: false, align: 'center' },
    { title: t('contact.Nearest Expiry Date') || 'Nearest Expiry Date', key: 'nearestExpiryDate', sortable: true, align: 'center' },
    { title: t('contact.Registrant') || 'Registrant', key: 'registrant', sortable: false },
    { title: t('contact.Option') || 'Option', key: 'option', sortable: false, align: 'center', width: '100px' },
]);

// 검색어 디바운스 타이머
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// 서버 페이징 조회 (검색어 없을 때만 사용)
const loadContacts = async (keepCurrentPage: boolean = false) => {
    try {
        const params: ContactListParams = {
            page: currentPage.value,
            limit: itemsPerPage.value,
        };

        if (selectedCategory.value) {
            params.category = selectedCategory.value;
        } else if (props.initialCategory) {
            params.category = props.initialCategory;
        }

        // 검색 모드일 때는 서버 검색을 fallback으로만 쓰기 때문에
        // 캐시가 없거나 로딩중일 때만 서버 검색 호출됨.
        const trimmedSearch = (searchQuery.value || '').trim();
        if (trimmedSearch) {
            params.search = trimmedSearch;
        }

        console.log('Loading contacts (server)...', params);
        await contactStore.fetchContacts(params);
        serverTotal.value = contactStore.total;

        if (keepCurrentPage) {
            const totalPages = Math.max(1, Math.ceil(serverTotal.value / itemsPerPage.value));
            if (currentPage.value > totalPages) {
                currentPage.value = totalPages;
                await contactStore.fetchContacts({ ...params, page: currentPage.value });
                serverTotal.value = contactStore.total;
            }
        }
    } catch (error: any) {
        console.error('Failed to load contacts:', error);
        alert(error?.message || 'Contact 목록을 불러오는데 실패했습니다.');
    }
};

// 백그라운드 프리페치 (all=true)
const prefetchAllContacts = async () => {
    if (isPrefetching.value) return;
    isPrefetching.value = true;
    try {
        console.log('[Prefetch] Starting full Contact list fetch...');
        const t0 = performance.now();

        // contactsApi를 직접 호출 (store는 건드리지 않음 - 서버페이징 결과 유지)
        const { contactsApi } = await import('../api/contactsApi');

        const params: ContactListParams = { all: true };
        if (props.initialCategory) params.category = props.initialCategory;

        const response = (await contactsApi.getContacts(params)) as any;
        const list = response?.contacts || [];

        allContactsCache.value = list;
        console.log(`[Prefetch] Cached ${list.length} contacts in ${Math.round(performance.now() - t0)}ms`);
    } catch (err: any) {
        console.warn('[Prefetch] Failed:', err?.message || err);
        // 실패해도 조용히 넘어감 (서버 검색 fallback이 동작함)
    } finally {
        isPrefetching.value = false;
    }
};

// 캐시 무효화 + 재프리페치
const invalidateCache = () => {
    allContactsCache.value = null;
    // 비동기로 재프리페치 시작
    prefetchAllContacts();
};

const handleSearch = () => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
        currentPage.value = 1;
        const trimmed = (searchQuery.value || '').trim();

        if (!trimmed) {
            // 검색어 클리어 → 서버페이징 복귀
            loadContacts();
            return;
        }

        // 캐시 있음 → 즉시 필터 (computed가 자동 반영, 추가 호출 불필요)
        if (allContactsCache.value) {
            console.log('[Search] Using cache');
            return;
        }

        // 캐시 없음 → 서버 검색 fallback (app-level LIKE, 느림)
        console.log('[Search] Cache not ready, fallback to server search');
        loadContacts();
    }, 300);
};

const handlePageChange = (page: number) => {
    currentPage.value = page;
    // 검색 모드 + 캐시 있음 → 재조회 불필요 (computed가 slice)
    if (isSearchMode.value && allContactsCache.value) return;
    loadContacts(true);
};

const handleItemsPerPageChange = (value: number) => {
    itemsPerPage.value = value;
    currentPage.value = 1;
    // 검색 모드 + 캐시 있음 → 재조회 불필요
    if (isSearchMode.value && allContactsCache.value) return;
    loadContacts();
};

// 모달 내부 라우팅 상태 관리
const modalRoute = ref<string>('list'); // 'list', 'create-individual', 'create-corporate', 'detail', 'edit'
const modalRouteParams = ref<any>({});

const goToDetail = (item: any) => {
    console.log('goToDetail called with:', item); // 디버깅용
    
    if (!item?.CONTACT_CD) {
        console.error('CONTACT_CD is missing in goToDetail:', item);
        return;
    }
    
    if (props.isModal) {
        // 모달 모드일 때는 모달 내부에서 라우팅
        modalRoute.value = 'detail';
        modalRouteParams.value = { id: item.CONTACT_CD };
    } else {
        // 일반 모드일 때는 일반 라우팅
        router.push({
            name: 'ContactDetail',
            params: { id: item.CONTACT_CD }
        });
    }
};

// 테이블 행 클릭 핸들러
const handleRowClick = (event: Event, { item }: { item: any }) => {
    console.log('Row clicked:', item); // 디버깅용
    console.log('CONTACT_CD:', item?.CONTACT_CD); // 디버깅용
    
    if (props.isModal) {
        // 모달 모드일 때는 항상 상세 페이지로 이동 (모달 안에서)
        goToDetail(item);
    } else {
        // 일반 모드일 때는 상세 페이지로 이동
        if (!item?.CONTACT_CD) {
            console.error('CONTACT_CD is missing:', item);
            return;
        }
        goToDetail(item);
    }
};

const goToCreateIndividual = createGuardedAction(
    '/Contact',
    'Add Individual',
    () => {
        if (props.isModal) {
            // 모달 내부에서 라우팅
            modalRoute.value = 'create-individual';
            modalRouteParams.value = {};
            return;
        }

        // 일반 모드 라우팅
        return router.push({ name: 'ContactCreateIndividual' }).then(() => {});
    }
);

const goToCreateCorporate = createGuardedAction(
    '/Contact',
    'Add Corporate',
    () => {
        if (props.isModal) {
            // 모달 내부에서 라우팅
            modalRoute.value = 'create-corporate';
            modalRouteParams.value = {};
            return;
        }

        // 일반 모드 라우팅
        return router.push({ name: 'ContactCreateCorporate' }).then(() => {});
    }
);

// 모달 내부에서 목록으로 돌아가기
const goBackToList = () => {
    modalRoute.value = 'list';
    modalRouteParams.value = {};
    // 목록 새로고침 + 캐시 무효화 (Create/Edit 후일 수 있음)
    loadContacts();
    invalidateCache();
};

// 상세 페이지에서 선택 이벤트 처리
const handleDetailSelect = (contact: any) => {
    // onSelect prop이 있으면 호출
    if (props.onSelect) {
        props.onSelect(contact);
    }
    emit('select', contact);
    // 모달 닫기
    emit('update:modelValue', false);
};

// File Upload 다이얼로그
const showFileUploadDialog = ref(false);

// Consolidate (Merge) — MY 관할에서만 노출 (버튼 + 리스트 체크박스)
const isJurisdictionMY = computed(() => {
    const jurisdiction = sessionStore.user?.customer?.jurisdiction || '';
    const upper = String(jurisdiction).trim().toUpperCase();
    return upper === 'MY' || upper === 'MALAYSIA';
});

// Consolidate (Merge) 다이얼로그
const showMergeDialog = ref(false);

// 리스트에서 병합 대상으로 체크한 CONTACT_CD 집합
//   v-data-table 의 show-select v-model:selected 는 item-value 배열을 사용.
const selectedForMerge = ref<string[]>([]);

// 다이얼로그에 넘길, 선택된 컨택트 객체 (선택 시점의 스냅샷 — 팝업이 목록을 다시 로드하지 않도록)
const mergePreselected = ref<any[]>([]);

const canConsolidate = computed(() => selectedForMerge.value.length >= 2);

// 선택된 항목들을 스냅샷으로 담아서 다이얼로그 오픈
function openConsolidateDialog() {
    if (!canConsolidate.value) return;
    const cdSet = new Set(selectedForMerge.value);
    // 현재 페이지 + 캐시 양쪽에서 조회 (선택 시점 페이지가 지나갔을 수 있으므로)
    const pool = new Map<string, any>();
    for (const c of paginatedContacts.value) {
        if (c?.CONTACT_CD && cdSet.has(c.CONTACT_CD)) pool.set(c.CONTACT_CD, c);
    }
    if (allContactsCache.value) {
        for (const c of allContactsCache.value) {
            if (c?.CONTACT_CD && cdSet.has(c.CONTACT_CD) && !pool.has(c.CONTACT_CD)) {
                pool.set(c.CONTACT_CD, c);
            }
        }
    }
    mergePreselected.value = Array.from(pool.values());
    showMergeDialog.value = true;
}

const onConsolidateClickGuard = createGuardedAction(
    '/Contact',
    'Consolidate',
    () => openConsolidateDialog(),
);

const handleMerged = () => {
    // 병합 완료 → 선택 초기화 + 목록 새로고침 + 캐시 무효화
    selectedForMerge.value = [];
    mergePreselected.value = [];
    loadContacts();
    invalidateCache();
};

// File Upload 핸들러
const handleFileUpload = () => {
    showFileUploadDialog.value = true;
};

// Guarded wrapper for File Upload (prevent opening popup when denied)
const onFileUploadClickGuard = createGuardedAction(
    '/Contact',
    'File Upload',
    () => handleFileUpload()
);


// 파일 업로드 완료 처리
const handleUploadComplete = () => {
    // Contact 목록 새로고침 + 캐시 무효화 (대량 추가될 수 있음)
    loadContacts();
    invalidateCache();
    // 모달 모드일 때는 File Upload 다이얼로그만 닫기 (모달은 유지)
    if (props.isModal) {
        showFileUploadDialog.value = false;
    }
};

// 번호 계산 (페이지네이션 고려)
const getRowNumber = (index: number) => {
    return (currentPage.value - 1) * itemsPerPage.value + index + 1;
};

// Category 변경 시 첫 페이지로 리셋 후 서버 재조회
watch(selectedCategory, () => {
    currentPage.value = 1;
    loadContacts();
});

// 세션 jurisdiction 기반 Registrant 공통코드 조회용 COUNTRY_CODE 결정
// - MY 세션: COUNTRY_CODE='MY'
// - 그 외: COUNTRY_CODE='SG'
const registrantCountryCode = computed(() => {
    const jurisdiction = sessionStore.user?.customer?.jurisdiction || '';
    const upper = String(jurisdiction).trim().toUpperCase();
    const isMY = upper === 'MY' || upper === 'MALAYSIA';
    return isMY ? 'MY' : 'SG';
});

// Registrant 공통코드 옵션 (A06: A06001/A06002 등)
const registrantOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

const loadRegistrantOptions = async () => {
    try {
        const response = await baseCodeApi.getCodesByMainCode(registrantCountryCode.value, 'A06', undefined);
        const codes = (response as any)?.codes || (response as any)?.data?.codes || (response as any)?.data?.data?.codes || (response as any)?.data || [];

        registrantOptions.value = (codes || [])
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim(),
            }))
            .filter((item: any) => item.TOTAL_CODE && item.CODE_NAME);
    } catch (e: any) {
        console.warn('Failed to load registrant options (A06)', e?.message || e);
        registrantOptions.value = [];
    }
};

// jurisdiction / language 변경 시 옵션 재조회
watch([registrantCountryCode, currentLanguage], async () => {
    await loadRegistrantOptions();
}, { immediate: true });

// Registrant 타입 표시 (CONTACT_REGISTER_TYPE, SOURCE_SYSTEM에 따라)
const getRegistrantDisplay = (contact: any) => {
    const code = String(contact?.CONTACT_REGISTER_TYPE || contact?.contactRegisterType || '').trim();
    const sourceSystem = String(contact?.SOURCE_SYSTEM || contact?.sourceSystem || '').trim().toUpperCase();
    if (!code) return '';

    // 우선 규칙: A06002 + SOURCE_SYSTEM(SPC/SPC_20260307) => From SPC
    if (code === 'A06002' && (sourceSystem === 'SPC' || sourceSystem === 'SPC_20260307')) {
        return 'From SPC';
    }

    const option = registrantOptions.value.find(opt => opt.TOTAL_CODE === code);
    if (option?.CODE_NAME) {
        return option.CODE_NAME;
    }

    // fallback (공통코드 조회 실패/미존재 시)
    if (code === 'A06001') {
        return t('contact.From BizFile') || 'From BizFile';
    } else if (code === 'A06002') {
        return t('contact.From Manual') || 'From Manual';
    }
    return '';
};

// 삭제 차단 모달
const deleteBlockerDialog = ref(false);
const deleteBlockerContactName = ref('');
const deleteBlockers = ref<ContactDeleteBlocker[]>([]);
const isCheckingDeleteBlockers = ref(false);

const getDeleteBlockerRoleLabel = (blocker: ContactDeleteBlocker) => {
    const key = String(blocker?.role || '').trim();
    // Company Relations와 동일: Officer는 POSITION_NAME 표시
    if (key === 'Officer' && blocker?.position) {
        return blocker.position;
    }
    const map: Record<string, string> = {
        Officer: t('contact.Officer') || 'Officer',
        Shareholder: t('contact.Shareholder') || 'Shareholder',
        Controller: t('contact.Controller') || 'Controller',
        'Beneficial Owner': t('contact.Beneficial Owner') || 'Beneficial Owner',
    };
    // Beneficial Owner: BO_JSON_DATA.IsBeneficialOwner=0 이면 position 이 'Legal Owner' 로 전달됨.
    if (key === 'Beneficial Owner' && blocker?.position) {
        const posMap: Record<string, string> = {
            'Legal Owner': t('contact.Legal Owner') || 'Legal Owner',
        };
        const posLabel = posMap[blocker.position] || blocker.position;
        return `${map['Beneficial Owner']} (${posLabel})`;
    }
    return map[key] || key;
};

// 삭제 핸들러
const handleDelete = async (contact: any) => {
    if (!contact?.CONTACT_CD) {
        console.error('CONTACT_CD is missing:', contact);
        return;
    }

    const contactName = contact.CONTACT_NAME || `${contact.FIRST_NAME || ''} ${contact.SURNAME || ''}`.trim() || t('contact.Contact') || 'Contact';

    isCheckingDeleteBlockers.value = true;
    try {
        const blockerResponse = await contactsApi.getDeleteBlockers(contact.CONTACT_CD);
        const responseData = blockerResponse as any;
        const blockers = responseData.blockers || responseData.data?.blockers || [];
        const canDelete = responseData.canDelete ?? responseData.data?.canDelete ?? blockers.length === 0;

        if (!canDelete && blockers.length > 0) {
            deleteBlockerContactName.value = contactName;
            deleteBlockers.value = blockers;
            deleteBlockerDialog.value = true;
            return;
        }
    } catch (error: any) {
        console.error('Failed to check delete blockers:', error);
        await alert({
            title: t('contact.Error') || 'Error',
            message: error?.message || t('contact.Failed to delete contact') || 'Failed to delete contact',
            type: 'error'
        });
        return;
    } finally {
        isCheckingDeleteBlockers.value = false;
    }
    
    const confirmed = await confirm({
        title: t('contact.Delete Contact') || 'Delete Contact',
        message: `${t('contact.Are you sure you want to delete this contact?') || 'Are you sure you want to delete this contact?'} "${contactName}"?`,
        type: 'warning'
    });

    if (!confirmed) {
        return;
    }

    try {
        // IS_DEL을 1로 업데이트 (soft delete)
        // 삭제 후에는 상세 조회를 다시 할 필요가 없으므로 skipReload 옵션 사용
        await contactStore.updateContact(contact.CONTACT_CD, { IS_DEL: true }, { skipReload: true });
        
        await alert({
            title: t('contact.Success') || 'Success',
            message: t('contact.Contact deleted successfully') || 'Contact deleted successfully',
            type: 'success'
        });

        // 목록 새로고침 (현재 페이지 유지) + 캐시 무효화
        loadContacts(true);
        invalidateCache();
    } catch (error: any) {
        console.error('Failed to delete contact:', error);
        const responseBlockers =
            error?.response?.data?.blockers ||
            error?.response?.data?.error?.blockers ||
            error?.response?.data?.data?.blockers ||
            [];
        if (Array.isArray(responseBlockers) && responseBlockers.length > 0) {
            deleteBlockerContactName.value = contactName;
            deleteBlockers.value = responseBlockers;
            deleteBlockerDialog.value = true;
            return;
        }
        await alert({
            title: t('contact.Error') || 'Error',
            message: error?.message || t('contact.Failed to delete contact') || 'Failed to delete contact',
            type: 'error'
        });
    }
};

// Guarded wrapper for Delete (prevent opening confirm when denied)
const onDeleteClickGuard = createGuardedAction(
    '/Contact',
    'Delete',
    (contact: any) => handleDelete(contact)
);

// initialSearch가 변경되면 searchQuery에 설정 후 서버 재조회
watch(() => props.initialSearch, (newSearch) => {
    if (props.isModal && newSearch) {
        searchQuery.value = newSearch;
        currentPage.value = 1;
        loadContacts();
    }
}, { immediate: true });

// 모달이 열릴 때 항상 목록 화면으로 리셋
watch(() => props.modelValue, (isOpen) => {
    if (props.isModal && isOpen) {
        modalRoute.value = 'list';
        modalRouteParams.value = {};
        if (props.initialSearch) {
            searchQuery.value = props.initialSearch;
            currentPage.value = 1;
        }
    }
    // 모달이 닫힐 때도 리셋
    if (props.isModal && !isOpen) {
        modalRoute.value = 'list';
        modalRouteParams.value = {};
    }
});

// 모달이 열릴 때 initialSearch가 있으면 검색어 설정
watch(() => props.modelValue, (isOpen) => {
    if (props.isModal && isOpen && props.initialSearch) {
        searchQuery.value = props.initialSearch;
        currentPage.value = 1;
    }
    // 모달이 열릴 때 항상 목록 화면으로 리셋
    if (props.isModal && isOpen) {
        modalRoute.value = 'list';
        modalRouteParams.value = {};
    }
});

// 모달이 닫힐 때 라우트 리셋
watch(() => props.modelValue, (isOpen) => {
    if (props.isModal && !isOpen) {
        modalRoute.value = 'list';
        modalRouteParams.value = {};
    }
});

// Contact 생성/수정 후 리스트 새로고침 이벤트 핸들러 (named reference - onBeforeUnmount 에서 제거 가능)
const handleContactListRefresh = () => {
    console.log('🔄 Contact list refresh event received, reloading contacts...');
    loadContacts();
    invalidateCache();
};

onMounted(() => {
    // 1) 현재 페이지 서버페이징 조회 (최우선, 빠른 렌더)
    loadContacts();

    // 2) 백그라운드에서 전체 Contact 프리페치 (검색 속도용)
    //    fire-and-forget: 실패해도 서버 검색 fallback이 동작
    prefetchAllContacts();

    // Contact 생성/수정 후 리스트로 돌아올 때를 감지하기 위한 이벤트 리스너
    window.addEventListener('contact-list-refresh', handleContactListRefresh);

    // 모달 모드이고 initialSearch가 있으면 검색어 설정
    if (props.isModal && props.initialSearch) {
        searchQuery.value = props.initialSearch;
    }
});

// 언마운트 시 명시적 리소스 해제 (Vue 가 GC 해주지만 안전장치)
onBeforeUnmount(() => {
    // 1) 이벤트 리스너 제거 (anonymous 가 아닌 named 이므로 정상 제거됨)
    window.removeEventListener('language-changed', handleLanguageChanged);
    window.removeEventListener('contact-list-refresh', handleContactListRefresh);

    // 2) 디바운스 타이머 정리 (페이지 이탈 직후 setTimeout 콜백이 튀는 것 방지)
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = null;
    }

    // 3) 전체 Contact 캐시 명시적 해제 (수천 건이면 메모리 점유가 있으므로 GC 힌트)
    allContactsCache.value = null;
});

// 라우트가 활성화될 때마다 데이터 다시 로드 (다른 페이지에서 돌아올 때)
onActivated(() => {
    loadContacts();
});

// 라우트 변경 감지 (Contact 페이지로 돌아올 때)
watch(() => route.path, (newPath, oldPath) => {
    if (newPath === '/Contact' && oldPath !== newPath) {
        console.log('🔄 Route changed to /Contact, reloading contacts...');
        loadContacts();
    }
}, { immediate: false });
</script>

<template>
    <!-- 모달 모드 -->
    <v-dialog
        v-if="isModal"
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
        max-width="1400px"
        width="95%"
        persistent
    >
        <v-card rounded="md">
            <v-card-title class="d-flex justify-space-between align-center pa-4">
                <div class="d-flex align-center gap-2">
                    <v-btn
                        v-if="modalRoute !== 'list'"
                        icon="mdi-arrow-left"
                        variant="text"
                        size="small"
                        @click="goBackToList"
                        class="mr-2"
                    />
                    <span class="text-h6">
                        <template v-if="modalRoute === 'list'">{{ t('contact.Contact List') || 'Contact List' }}</template>
                        <template v-else-if="modalRoute === 'create-individual'">{{ t('contact.Create Individual Contact') || 'Create Individual Contact' }}</template>
                        <template v-else-if="modalRoute === 'create-corporate'">{{ t('contact.Create Corporate Contact') || 'Create Corporate Contact' }}</template>
                        <template v-else-if="modalRoute === 'detail'">{{ t('contact.Contact Detail') || 'Contact Detail' }}</template>
                    </span>
                </div>
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    @click="emit('update:modelValue', false)"
                />
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
                <!-- 목록 화면 -->
                <template v-if="modalRoute === 'list'">
                    <!-- 검색 필드 및 버튼 -->
                    <div class="d-flex justify-space-between align-center mb-4">
                        <v-text-field
                            v-model="searchQuery"
                            append-inner-icon="mdi-magnify"
                            :label="t('contact.Search') || 'Search'"
                            single-line
                            hide-details
                            class="search-field"
                            style="min-width: 300px; max-width: 400px;"
                            @input="handleSearch"
                            @keyup.enter="handleSearch"
                        />
                        <div class="d-flex gap-2">
                            <v-btn 
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="Add Individual"
                                color="primary" 
                                variant="flat" 
                                dark 
                                class="main-button"
                                @click="goToCreateIndividual"
                            >
                                <v-icon left>mdi-plus</v-icon>
                                {{ t('contact.Add Individual') || 'Add Individual' }}
                            </v-btn>
                            <v-btn 
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="Add Corporate"
                                color="primary" 
                                variant="flat" 
                                dark 
                                class="main-button"
                                @click="goToCreateCorporate"
                            >
                                <v-icon left>mdi-plus</v-icon>
                                {{ t('contact.Add Corporate') || 'Add Corporate' }}
                            </v-btn>
                            <v-btn
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="File Upload"
                                variant="outlined"
                                color="primary"
                                class="file-upload-btn"
                                @click="onFileUploadClickGuard"
                            >
                                <v-icon left color="success">mdi-microsoft-excel</v-icon>
                                File Upload
                            </v-btn>
                        </div>
                    </div>
                    
                    <!-- 테이블 -->
                <v-data-table
                    :items="paginatedContacts"
                    :headers="headers"
                    :loading="contactStore.isLoading"
                    :items-per-page="-1"
                    hide-default-footer
                    @click:row="handleRowClick"
                    class="elevation-1"
                >
                    <template #item.no="{ index }">
                        {{ getRowNumber(index) }}
                    </template>
                    
                    <template #item.CATEGORY_CD="{ item }">
                        <v-chip
                            :color="item.CATEGORY_CD === 'B09002' ? 'primary' : 'secondary'"
                            size="small"
                        >
                            {{ item.CATEGORY_CD === 'B09002' 
                                ? (t('contact.Individual') || 'Individual')
                                : (t('contact.Corporate') || 'Corporate')
                            }}
                        </v-chip>
                    </template>

                    <template #item.CONTACT_NAME="{ item }">
                        {{ item.CONTACT_NAME || `${item.FIRST_NAME || ''} ${item.SURNAME || ''}`.trim() || 'Corp or Individual Name' }}
                    </template>

                    <template #item.CONTACT_EMAIL="{ item }">
                        {{ item.CONTACT_EMAIL || '-' }}
                    </template>

                    <template #item.CONTACT_REG_NO="{ item }">
                        <div class="text-center">
                            {{ item.CONTACT_REG_NO || '-' }}
                        </div>
                    </template>

                    <template #item.nearestExpiryDate="{ item }">
                        <div class="text-center">
                            {{ (item as any).nearestExpiryDate || '-' }}
                        </div>
                    </template>

                    <template #item.registrant="{ item }">
                        {{ getRegistrantDisplay(item) || (item as any).registrant || '' }}
                    </template>

                    <template #item.option="{ item }">
                        <div class="text-center">
                            <v-btn
                                v-permission-button
                                data-menu-key="/Contact"
                                data-button-name="Delete"
                                v-if="item.CONTACT_REGISTER_TYPE === 'A06002'"
                                icon="mdi-delete"
                                size="small"
                                variant="text"
                                color="error"
                                @click.stop="onDeleteClickGuard(item)"
                            >
                                <v-icon>mdi-delete</v-icon>
                                <v-tooltip activator="parent" location="top">
                                    {{ t('contact.Delete') || 'Delete' }}
                                </v-tooltip>
                            </v-btn>
                            <span v-else>-</span>
                        </div>
                    </template>
                    
                    <template #no-data>
                        <div class="text-center py-8">
                            {{ t('contact.No contacts found') || 'No contacts found' }}
                        </div>
                    </template>
                    
                    <template #bottom>
                        <div class="pagination-container">
                            <div class="pagination-content">
                                <v-select
                                    :items="[10, 20, 50]"
                                    v-model="itemsPerPage"
                                    density="compact"
                                    variant="outlined"
                                    hide-details
                                    class="items-per-page-select"
                                    @update:model-value="handleItemsPerPageChange"
                                />
                                <v-pagination
                                    v-model="currentPage"
                                    :length="Math.max(1, Math.ceil(filteredTotal / itemsPerPage))"
                                    :total-visible="7"
                                    density="compact"
                                    @update:model-value="handlePageChange"
                                />
                            </div>
                        </div>
                    </template>
                </v-data-table>
                </template>
                
                <!-- 생성/수정/상세 화면 -->
                <template v-else>
                    <div v-if="modalRoute === 'create-individual'">
                        <ContactCreateIndividualPage />
                    </div>
                    <div v-else-if="modalRoute === 'create-corporate'">
                        <ContactCreateCorporatePage />
                    </div>
                    <div v-else-if="modalRoute === 'detail'">
                        <ContactDetailPage 
                            :contact-id="modalRouteParams.id"
                            :contact-list-menu-mode="true"
                            :is-modal="true"
                            :on-select="props.onSelect"
                            @back="goBackToList"
                            @select="handleDetailSelect"
                        />
                    </div>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
    
    <!-- 일반 모드 -->
    <div v-else class="page-center">
        <v-row>
            <v-col cols="12">
                <!-- 카드 밖 상단 제목/브레드크럼 -->
                <div class="mb-2">
                    <div class="page-title">{{ t('contact.Contact') || 'Contact' }}</div>
                    <div class="breadcrumb-line">
                        <span class="breadcrumb-text">{{ t('contact.Home') || 'Home' }} /</span>
                        <RouterLink to="/Contact" class="breadcrumb-link">{{ t('contact.Contact') || 'Contact' }}</RouterLink>
                    </div>
                </div>

                <v-card rounded="md" class="contact-card">
                    <v-card-title class="d-flex align-center contact-card-title-bar">
                        <div class="contact-title-icon">
                            <v-icon size="20" color="primary">mdi-card-account-details-outline</v-icon>
                        </div>
                        <span class="card-title">{{ t('contact.Contact List') || 'Contact List' }}</span>
                    </v-card-title>
                    <v-divider />

                    <v-card-text class="card-content">
                    <!-- 테이블 -->
                    <v-data-table
                        :items="paginatedContacts"
                        :headers="headers"
                        :loading="contactStore.isLoading"
                        class="rounded-md datatabels contact-data-table"
                        item-value="CONTACT_CD"
                        color="primary"
                        :items-per-page="itemsPerPage"
                        :page="currentPage"
                        :items-length="filteredTotal"
                        hide-default-footer
                        :show-select="isJurisdictionMY"
                        v-model="selectedForMerge"
                        @click:row="handleRowClick"
                    >
                        <template #top>
                            <v-toolbar class="bg-surface search-toolbar" flat>
                                <div class="d-md-flex block justify-space-between w-100 pt-4 pb-6 align-center">
                                    <div class="d-flex align-center gap-3 mb-md-0 mb-3">
                                        <v-select
                                            v-if="false"
                                            v-model="selectedCategory"
                                            :items="[
                                                { title: t('contact.Individual') || 'Individual', value: 'B09002' },
                                                { title: t('contact.Corporate') || 'Corporate', value: 'B09001' },
                                            ]"
                                            :label="t('contact.Category') || 'Category'"
                                            clearable
                                            append-inner-icon="mdi-chevron-down"
                                            hide-details
                                            class="mb-md-0 mb-3"
                                            style="min-width: 200px; max-width: 250px;"
                                            @update:model-value="handleSearch"
                                        />
                                        <v-text-field
                                            v-model="searchQuery"
                                            append-inner-icon="mdi-magnify"
                                            :label="t('contact.Search') || 'Search'"
                                            single-line
                                            hide-details
                                            class="mb-md-0 mb-3 search-field"
                                            style="min-width: 300px; max-width: 400px;"
                                            placeholder="Search by name or registration number"
                                            @input="handleSearch"
                                            @keyup.enter="handleSearch"
                                        />
                                    </div>
                                    <div class="d-flex gap-2">
                                        <v-btn
                                            v-if="isJurisdictionMY"
                                            v-permission-button
                                            data-menu-key="/Contact"
                                            data-button-name="Consolidate"
                                            variant="outlined"
                                            color="primary"
                                            class="consolidate-btn"
                                            :disabled="!canConsolidate"
                                            @click="onConsolidateClickGuard"
                                        >
                                            <v-icon left color="primary">mdi-merge</v-icon>
                                            {{ t('contact.Consolidate') || 'Consolidate' }}
                                            <v-chip
                                                v-if="selectedForMerge.length > 0"
                                                size="x-small"
                                                color="primary"
                                                variant="flat"
                                                class="ml-2"
                                            >
                                                {{ selectedForMerge.length }}
                                            </v-chip>
                                        </v-btn>
                                        <v-btn
                                            v-permission-button
                                            data-menu-key="/Contact"
                                            data-button-name="File Upload"
                                            variant="outlined"
                                            color="primary"
                                            class="file-upload-btn"
                                            @click="onFileUploadClickGuard"
                                        >
                                            <v-icon left color="success">mdi-microsoft-excel</v-icon>
                                            File Upload
                                        </v-btn>
                                        <v-btn 
                                            v-permission-button
                                            data-menu-key="/Contact"
                                            data-button-name="Add Individual"
                                            color="primary" 
                                            variant="flat" 
                                            dark 
                                            class="main-button"
                                            @click="goToCreateIndividual"
                                        >
                                            <v-icon left>mdi-plus</v-icon>
                                            {{ t('contact.Add Individual') || 'Add Individual' }}
                                        </v-btn>
                                        <v-btn 
                                            v-permission-button
                                            data-menu-key="/Contact"
                                            data-button-name="Add Corporate"
                                            color="primary" 
                                            variant="flat" 
                                            dark 
                                            class="main-button"
                                            @click="goToCreateCorporate"
                                        >
                                            <v-icon left>mdi-plus</v-icon>
                                            {{ t('contact.Add Corporate') || 'Add Corporate' }}
                                        </v-btn>
                                    </div>
                                </div>
                            </v-toolbar>
                        </template>
                        <template #item.no="{ index }">
                            {{ getRowNumber(index) }}
                        </template>
                        
                        <template #item.CATEGORY_CD="{ item }">
                            <v-chip
                                :color="item.CATEGORY_CD === 'B09002' ? 'primary' : 'secondary'"
                                size="small"
                            >
                                {{ item.CATEGORY_CD === 'B09002' 
                                    ? (t('contact.Individual') || 'Individual')
                                    : (t('contact.Corporate') || 'Corporate')
                                }}
                            </v-chip>
                        </template>

                        <template #item.CONTACT_NAME="{ item }">
                            {{ item.CONTACT_NAME || `${item.FIRST_NAME || ''} ${item.SURNAME || ''}`.trim() || 'Corp or Individual Name' }}
                        </template>

                        <template #item.CONTACT_EMAIL="{ item }">
                            {{ item.CONTACT_EMAIL || '-' }}
                        </template>

                        <template #item.CONTACT_REG_NO="{ item }">
                            <div class="text-center">
                                {{ item.CONTACT_REG_NO || '-' }}
                            </div>
                        </template>

                        <template #item.nearestExpiryDate="{ item }">
                            <div class="text-center">
                                {{ (item as any).nearestExpiryDate || '-' }}
                            </div>
                        </template>

                        <template #item.registrant="{ item }">
                            {{ getRegistrantDisplay(item) || (item as any).registrant || '' }}
                        </template>

                        <template #item.option="{ item }">
                            <div class="text-center">
                                <v-btn
                                    v-permission-button
                                    data-menu-key="/Contact"
                                    data-button-name="Delete"
                                    v-if="item.CONTACT_REGISTER_TYPE === 'A06002'"
                                    icon="mdi-delete"
                                    size="small"
                                    variant="text"
                                    color="error"
                                    @click.stop="onDeleteClickGuard(item)"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                    <v-tooltip activator="parent" location="top">
                                        {{ t('contact.Delete') || 'Delete' }}
                                    </v-tooltip>
                                </v-btn>
                                <span v-else>-</span>
                            </div>
                        </template>
                        
                        <template #no-data>
                            <div class="text-center py-8">{{ t('contact.No contacts found') || 'No contacts found' }}</div>
                        </template>
                        
                        <template #bottom>
                            <div class="pagination-container">
                                <div class="pagination-content">
                                    <v-select
                                        :items="[10, 20, 50]"
                                        v-model="itemsPerPage"
                                        density="compact"
                                        variant="outlined"
                                        hide-details
                                        class="items-per-page-select"
                                        @update:model-value="handleItemsPerPageChange"
                                    />
                                    <v-pagination
                                        v-model="currentPage"
                                        :length="Math.max(1, Math.ceil(filteredTotal / itemsPerPage))"
                                        :total-visible="7"
                                        density="compact"
                                        @update:model-value="handlePageChange"
                                    />
                                </div>
                            </div>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
    
    <!-- File Upload Dialog -->
    <ContactFileUploadPage
        v-model="showFileUploadDialog"
        @upload-complete="handleUploadComplete"
    />

    <!-- Consolidate (Merge) Dialog — 리스트에서 체크한 컨택트를 스냅샷으로 전달 -->
    <ContactMergeDialog
        v-model="showMergeDialog"
        :preselected="mergePreselected"
        @merged="handleMerged"
    />

    <!-- 삭제 차단 모달 -->
    <v-dialog v-model="deleteBlockerDialog" max-width="560" persistent>
        <v-card rounded="md">
            <v-card-title class="d-flex align-center gap-2 pa-4">
                <v-icon color="warning">mdi-alert-circle-outline</v-icon>
                <span>{{ t('contact.Cannot Delete Contact') || 'Cannot Delete Contact' }}</span>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-4">
                <p class="mb-3">
                    {{
                        t('contact.This contact is linked to one or more entities and cannot be deleted.') ||
                        'This contact is linked to one or more entities and cannot be deleted.'
                    }}
                </p>
                <p v-if="deleteBlockerContactName" class="mb-4 font-weight-medium">
                    {{ deleteBlockerContactName }}
                </p>
                <v-table density="compact" class="delete-blocker-table">
                    <thead>
                        <tr>
                            <th>{{ t('contact.Entity') || 'Entity' }}</th>
                            <th>{{ t('contact.Role') || 'Role' }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(blocker, idx) in deleteBlockers" :key="`${blocker.entityCd}-${blocker.role}-${blocker.position || ''}-${idx}`">
                            <td>{{ blocker.entityName || blocker.entityCd }}</td>
                            <td>{{ getDeleteBlockerRoleLabel(blocker) }}</td>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn color="primary" variant="flat" @click="deleteBlockerDialog = false">
                    {{ t('contact.OK') || 'OK' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    </div>
</template>

<style scoped>
.delete-blocker-table :deep(thead th) {
    font-size: 13px;
    font-weight: 600;
}

.delete-blocker-table :deep(tbody td) {
    font-size: 14px;
}

/* 폰트 패밀리 - Inter */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.v-data-table :deep(tbody tr) {
    cursor: pointer;
}

/* Vuetify 기본값과 일치하는 스타일 */
.page-wrapper {
    padding: 24px;
}

.v-card {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 타이틀 스타일 - 21px */
.page-title {
    font-size: 21px;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.2;
    white-space: nowrap;
}

.contact-card {
    overflow: hidden;
}

.contact-card :deep(.contact-card-title-bar) {
    background: #1976d2;
    color: #ffffff !important;
    padding: 12px 20px !important;
    min-height: 48px;
}

.contact-title-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #e3f2fd;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex: 0 0 auto;
}

.contact-card :deep(.contact-card-title-bar) .card-title {
    color: #ffffff !important;
    font-size: 21px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

/* 브레드크럼 텍스트 - Content B (12px) */
.breadcrumb-line {
    white-space: nowrap;
}

.breadcrumb-text {
    font-size: 12px;
    color: rgb(var(--v-theme-on-surface));
}

.breadcrumb-link {
    font-size: 12px;
    color: #1B84FF;
    font-weight: 500;
    text-decoration: none;
}

.breadcrumb-line {
    margin-top: 4px;
}

/* 검색 필드 레이블 - Content A (14px) */
.search-field :deep(.v-field-label) {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
}

.search-field :deep(.v-field__input) {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
}

.search-field {
    margin-left: 8px;
}

/* 버튼 텍스트 - Content A (14px) */
.v-btn {
    text-transform: none;
    font-weight: 500;
    font-size: 14px;
}

/* File Upload 버튼 스타일 (연한 파란색, 흰색 테두리) */
.file-upload-btn {
    background-color: #e3f2fd !important;
    border-color: #ffffff !important;
    border-width: 1px !important;
    border-style: solid !important;
}

.tdrive-btn {
    background-color: #f1f5ff !important;
    border-color: #ffffff !important;
    border-width: 1px !important;
    border-style: solid !important;
    font-weight: 600 !important;
}

/* 메인 버튼 스타일 (Add Individual/Add Corporate) - 14px / 1B84FF */
.main-button {
    background-color: #1B84FF !important;
    color: white !important;
    font-size: 14px !important;
    font-weight: 500 !important;
}

.main-button:hover {
    background-color: #1568CC !important;
}

/* 테이블 헤더 - Contents B (12px) */
.contact-data-table :deep(thead th) {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: rgb(var(--v-theme-on-surface)) !important;
}

/* 테이블 바디 - Content A (14px) */
.contact-data-table :deep(tbody td) {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
}

/* 테이블 호버 효과 */
.contact-data-table :deep(tbody tr:hover) {
    background-color: rgba(27, 132, 255, 0.04) !important;
}

/* 다크모드 호버 효과 */
:deep(.v-theme--dark) .contact-data-table :deep(tbody tr:hover) {
    background-color: rgba(255, 255, 255, 0.08) !important;
}

.contact-data-table :deep(tbody tr) {
    cursor: pointer;
}

/* 입력 필드 포커스 컬러 */
.search-field :deep(.v-field--focused .v-field__outline) {
    color: #1B84FF !important;
}

/* 카드 컨텐츠 */
.card-content {
    padding: 12px 24px 24px !important;
}

/* 검색 툴바 상단 여백 */
.search-toolbar {
    padding-top: 0 !important;
    overflow: visible !important;
}

/* 페이지네이션 - Content A (14px) */
.pagination-container {
    padding: 16px 0;
}

.pagination-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
}

.items-per-page-select {
    max-width: 80px;
    min-width: 80px;
}

.items-per-page-select :deep(.v-field__input),
.pagination-container :deep(.v-pagination__item) {
    font-size: 14px;
    color: rgb(var(--v-theme-on-surface));
}

/* 페이지네이션 active 컬러 */
.pagination-container :deep(.v-pagination__item--active) {
    background-color: #1B84FF !important;
    color: white !important;
}

/* File Upload 버튼 스타일 개선 */
.file-upload-btn {
    background-color: #D5EDFF !important;
    color: #1B84FF !important;
    border-color: #1B84FF !important;
}

.file-upload-btn:hover {
    background-color: #C2E3FF !important;
}

/* Consolidate 버튼 - File Upload 옆에 두는 병합 액션 */
.consolidate-btn {
    background-color: #E8F0FE !important;
    color: #1B84FF !important;
    border-color: #1B84FF !important;
}

.consolidate-btn:hover {
    background-color: #D0DEFD !important;
}
</style>

